/**
 * Squad mapping utilities for converting API data to UI components.
 */

import type { FUSportsSquadMember } from "fansunited-data-layer";
import type { SquadMember } from "fansunited-sports-ui";
import type { Labels } from "@/lib/labels/types";

/**
 * Generates an SVG data URL displaying a shirt number.
 * Used as a player image placeholder when player photos are disabled.
 */
export function shirtNumberSvg(number?: string | number, bgColor = "#374151", textColor = "#ffffff"): string {
    const display = number != null ? String(number) : "–";
    const bg = encodeURIComponent(bgColor);
    const fg = encodeURIComponent(textColor);
    return `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'%3E%3Crect width='100' height='100' rx='50' fill='${bg}'/%3E%3Ctext x='50' y='54' text-anchor='middle' dominant-baseline='central' font-family='system-ui,sans-serif' font-weight='700' font-size='40' fill='${fg}'%3E${encodeURIComponent(display)}%3C/text%3E%3C/svg%3E`;
}

/** Player card stats labels type */
type PlayerCardStatsLabels = Labels["teamPage"]["playerCardStats"];

/** Player positions labels type */
type PlayerPositionsLabels = Labels["teamPage"]["playerPositions"];

/**
 * Translates an API position string to a localized label.
 * Normalizes the position string by removing spaces and hyphens, then looks up the translation.
 * Falls back to the original position if no translation is found.
 *
 * @param position - The position string from the API (e.g., "Centre-Back", "Defensive Midfielder")
 * @param labels - The playerPositions labels object with translations
 * @returns The translated position string, or the original if no translation found
 */
export function translatePosition(position: string | undefined, labels?: PlayerPositionsLabels): string {
    if (!position) return "";
    if (!labels) return position;

    // Normalize the position: lowercase, remove spaces and hyphens
    const normalizedPosition = position.toLowerCase().replace(/[\s-]/g, "") as keyof PlayerPositionsLabels;

    // Look up the translation, fall back to original position
    return labels[normalizedPosition] ?? position;
}

/**
 * Maps position from data layer to position group for UI
 */
export function getPositionGroup(position?: string): "GK" | "DEF" | "MID" | "FWD" {
    if (!position) return "MID";

    const pos = position.toLowerCase();

    if (pos.includes("goalkeeper") || pos.includes("keeper")) {
        return "GK";
    }

    if (
        pos.includes("defender") ||
        pos.includes("back") ||
        pos.includes("centre-back") ||
        pos.includes("center-back")
    ) {
        return "DEF";
    }

    if (pos.includes("forward") || pos.includes("striker") || pos.includes("winger") || pos.includes("attacker")) {
        return "FWD";
    }

    return "MID";
}

/**
 * Build a slim stats array for the match-aggregated card (non-WC teams). Only
 * the fields the SDK can extract from `mainEvents` are available — apps,
 * goal contributions, cards. Minutes/shots/clean sheets aren't in match
 * events. Goalkeepers drop goals/assists since those are essentially always
 * zero and reading them as headline stats is misleading.
 */
function buildSlimStats(
    positionGroup: "GK" | "DEF" | "MID" | "FWD",
    seasonStats?: FUSportsSquadMember["seasonStatistics"],
    labels?: PlayerCardStatsLabels
): SquadMember["stats"] {
    const stats = seasonStats;
    const l = labels;
    const base = [
        { id: "appearances", label: l?.appearances ?? "Apps", value: stats?.appearances ?? 0 },
    ];
    if (positionGroup !== "GK") {
        base.push(
            { id: "goals", label: l?.goals ?? "Goals", value: stats?.goals ?? 0 },
            { id: "assists", label: l?.assists ?? "Assists", value: stats?.assists ?? 0 },
        );
    }
    base.push(
        { id: "yellowCards", label: l?.yellow ?? "Yellow", value: stats?.yellowCards ?? 0 },
        { id: "redCards", label: l?.red ?? "Red", value: stats?.redCards ?? 0 },
    );
    return base;
}

/**
 * Build the per-position stat set for a player card.
 *
 * Sourced from the FU Sports hydrated feed's athleteStats bundle (see
 * src/utilities/data/team/__tests__/stats-keys.test.ts for the exact keys).
 * Each card is 8 cells; the set per position is curated — no card ever
 * shows a stat the feed doesn't publish.
 *
 * Averages (`minsPerMatch`, `minsPerGoal`) are computed inline from
 * summed totals rather than read from the feed's per-row average fields,
 * so a player with rows across multiple competitions gets a true all-up
 * average rather than the first row's local one.
 */
function buildComprehensiveStats(
    positionGroup: "GK" | "DEF" | "MID" | "FWD",
    seasonStats?: FUSportsSquadMember["seasonStatistics"],
    labels?: PlayerCardStatsLabels
): SquadMember["stats"] {
    const s = seasonStats;
    const l = labels;

    const appearances = s?.appearances ?? 0;
    const substituteIn = s?.substituteIn ?? 0;
    const started = s?.started ?? 0;
    const minutes = s?.minutes ?? 0;
    const goals = s?.goals ?? 0;
    const assists = s?.assists ?? 0;
    const pensScored = s?.penaltyGoals ?? 0;
    const pensMissed = s?.penaltiesMissed ?? 0;
    const ownGoals = s?.ownGoals ?? 0;
    const yellow = s?.yellowCards ?? 0;
    const red = s?.redCards ?? 0;
    const cleansheets = s?.cleansheets ?? 0;
    const conceded = s?.conceded ?? 0;

    const avg = (n: number, d: number) => (d > 0 ? Math.round((n / d) * 10) / 10 : 0);

    switch (positionGroup) {
        case "GK":
            return [
                { id: "appsSub", label: l?.appsSub ?? "Apps (Sub)", value: `${appearances} (${substituteIn})` },
                { id: "starts", label: l?.starts ?? "Starts", value: started },
                { id: "minutes", label: l?.minutes ?? "Minutes", value: minutes },
                { id: "cleansheets", label: l?.cleanSheets ?? "Clean Sheets", value: cleansheets },
                { id: "conceded", label: l?.conceded ?? "Conceded", value: conceded },
                { id: "minsPerMatch", label: l?.minsPerMatch ?? "Mins/Match", value: avg(minutes, appearances) },
                { id: "yellow", label: l?.yellow ?? "Yellow", value: yellow },
                { id: "red", label: l?.red ?? "Red", value: red },
            ];
        case "DEF":
            return [
                { id: "appsSub", label: l?.appsSub ?? "Apps (Sub)", value: `${appearances} (${substituteIn})` },
                { id: "starts", label: l?.starts ?? "Starts", value: started },
                { id: "minutes", label: l?.minutes ?? "Minutes", value: minutes },
                { id: "cleansheets", label: l?.cleanSheets ?? "Clean Sheets", value: cleansheets },
                { id: "goals", label: l?.goals ?? "Goals", value: goals },
                { id: "assists", label: l?.assists ?? "Assists", value: assists },
                { id: "yellow", label: l?.yellow ?? "Yellow", value: yellow },
                { id: "red", label: l?.red ?? "Red", value: red },
            ];
        case "MID":
            return [
                { id: "appsSub", label: l?.appsSub ?? "Apps (Sub)", value: `${appearances} (${substituteIn})` },
                { id: "starts", label: l?.starts ?? "Starts", value: started },
                { id: "minutes", label: l?.minutes ?? "Minutes", value: minutes },
                { id: "goals", label: l?.goals ?? "Goals", value: goals },
                { id: "assists", label: l?.assists ?? "Assists", value: assists },
                { id: "pensScored", label: l?.pensScored ?? "Pens Scored", value: pensScored },
                { id: "yellow", label: l?.yellow ?? "Yellow", value: yellow },
                { id: "red", label: l?.red ?? "Red", value: red },
            ];
        case "FWD":
            return [
                { id: "appsSub", label: l?.appsSub ?? "Apps (Sub)", value: `${appearances} (${substituteIn})` },
                { id: "starts", label: l?.starts ?? "Starts", value: started },
                { id: "minutes", label: l?.minutes ?? "Minutes", value: minutes },
                { id: "goals", label: l?.goals ?? "Goals", value: goals },
                { id: "assists", label: l?.assists ?? "Assists", value: assists },
                { id: "pens", label: l?.pensScored ?? "Pens Scored", value: `${pensScored} (${pensMissed})` },
                { id: "minsPerGoal", label: l?.minsPerGoal ?? "Mins/Goal", value: avg(minutes, goals) },
                {
                    id: "cards",
                    label: `${l?.yellow ?? "Yellow"} / ${l?.red ?? "Red"}`,
                    value: `${yellow} / ${red}`,
                },
            ];
    }
    // Unreachable in practice — exhaustive switch above.
    void ownGoals;
    return [];
}

/**
 * Maps a squad member from the data layer to the UI component format
 */
export function mapSquadMemberToUI(
    member: FUSportsSquadMember,
    labels?: PlayerCardStatsLabels,
    options?: {
        playerUrlBuilder?: (playerId: string) => string;
        playerIdMap?: Record<string, string>;
        allowPlayerPhotos?: boolean;
        primaryColor?: string;
        primaryForeground?: string;
        isNationalTeam?: boolean;
    }
): SquadMember {
    const positionGroup = getPositionGroup(member.position);
    const stats = options?.isNationalTeam
        ? buildSlimStats(positionGroup, member.seasonStatistics, labels)
        : buildComprehensiveStats(positionGroup, member.seasonStatistics, labels);

    let nationality: SquadMember["nationality"] = undefined;
    if (member.country) {
        nationality = {
            name: member.country.name,
            code: member.country.code,
            flagUrl:
                member.country.flag ||
                (member.country.code ? `https://flagcdn.com/w40/${member.country.code.toLowerCase()}.png` : undefined),
        };
    }

    return {
        id: member.id,
        name: member.name,
        shortName: member.name.split(" ").pop() || member.name,
        jerseyNumber: member.shirtNumber,
        positionGroup,
        position: member.position || positionGroup,
        imageUrl:
            options?.allowPlayerPhotos !== false
                ? member.assets?.photo || member.assets?.photoTransparent
                : shirtNumberSvg(member.shirtNumber, options?.primaryColor ?? undefined, options?.primaryForeground ?? undefined),
        nationality,
        stats,
        href: options?.playerUrlBuilder
            ? options.playerUrlBuilder(options.playerIdMap?.[member.id] || member.id)
            : undefined,
    };
}

/**
 * Maps an array of squad members from the data layer to UI format
 * Orders players by minutes played (descending) before grouping by position
 */
export function mapSquadToUI(
    squadMembers: FUSportsSquadMember[],
    labels?: PlayerCardStatsLabels,
    options?: {
        playerUrlBuilder?: (playerId: string) => string;
        playerIdMap?: Record<string, string>;
        allowPlayerPhotos?: boolean;
        primaryColor?: string;
        primaryForeground?: string;
        isNationalTeam?: boolean;
    }
): SquadMember[] {
    const mappedMembers = squadMembers.map((member) => mapSquadMemberToUI(member, labels, options));

    // National-team cards don't expose minutes (not in match events), so fall
    // back to appearances for ordering.
    const sortKey = options?.isNationalTeam ? "appearances" : "minutes";
    const sortedMembers = mappedMembers.sort((a, b) => {
        const aValue = a.stats?.find((s) => s.id === sortKey)?.value ?? 0;
        const bValue = b.stats?.find((s) => s.id === sortKey)?.value ?? 0;
        return (bValue as number) - (aValue as number);
    });

    return sortedMembers;
}
